/* eslint-disable max-statements */
/* eslint-disable camelcase */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { selectCurrentEmpid } from '../../../../../pages/login/authSlice';
import { useGetCoachingActionPlansQuery } from '../../../activeActionPlans/activeActionPlansSlice';
import { useGetCAPsessionQuery } from '../../../coachingActionPlanSlice';
import { SessionReadOnlyModal } from '../../../coachingHistory/SessionReadOnlyModal';
import { CaMenuTabindex, CoachingNextBestActionType } from '../../../../../utils/typeConstants';
import { metricValueFormat } from '../../../../../utils/metricValueFormat';
import './actionPlans.scss';
import warningImage from '../../../../../assets/warningImage.png';
import { CAMENU } from '../../../../../constants';
import { getMetricRecommendationImage } from '../../../../../utils/metricFormatters';
import DeleteDraftCAP from '../../../deleteDraftCAP/DeleteDraftCAP';

const STEPS = { PREPARE: 0, ANALYZE: 1, COACH: 2, FOLLOW_UP: 3 } as const;

const getDefaultFromDate = (): Date => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d;
};

interface ActionPlansProps {
    scorecardVersionId: number;
    teamLeadEmployeeId?: number;
    via?: string;
    setActionPlanId?: React.Dispatch<React.SetStateAction<number | null>>;
    setSessionId?: React.Dispatch<React.SetStateAction<number | null>>;
    setShowAcknowledge?: React.Dispatch<React.SetStateAction<boolean>>;
    emptyMessage?: string;
}

const ActionPlans: React.FC<ActionPlansProps> = ({ scorecardVersionId, via, teamLeadEmployeeId, setActionPlanId, setSessionId, setShowAcknowledge, emptyMessage }) => {
    const navigate = useNavigate();
    const loggedEmpNum = useSelector(selectCurrentEmpid);

    // ── Filter state ───────────────────────────────────────────────────
    const [filterStatus, setFilterStatus] = useState<string[]>([]);
    const [filterAssociate, setFilterAssociate] = useState<string | null>(null);
    const [filterMetric, setFilterMetric] = useState<string | null>(null);
    const [filterAcknowledgment, setFilterAcknowledgment] = useState<string | null>(null);
    const [filterCommitmentAttained, setFilterCommitmentAttained] = useState<string | null>(null);
    const [fromDateFilter, setFromDateFilter] = useState<Date>(getDefaultFromDate);
    const [toDateFilter, setToDateFilter] = useState<Date>(new Date());
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [actionPlanToDelete, setActionPlanToDelete] = useState<number | null>(null);

    // Stable ISO strings so RTK Query args don't change on every render
    const fromDateISO = useMemo(() => {
        const d = fromDateFilter;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, [fromDateFilter]);

    const toDateISO = useMemo(() => {
        const d = toDateFilter;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, [toDateFilter]);

    // ── API ────────────────────────────────────────────────────────────
    const { data: actionPlansData, isLoading } = useGetCoachingActionPlansQuery(
        {
            employeeNumber: via === 'managerCAP' ? teamLeadEmployeeId : loggedEmpNum,
            scorecardVersionId,
            fromDate: fromDateISO,
            toDate: toDateISO,
        },
        { skip: !loggedEmpNum || !scorecardVersionId || (via === 'managerCAP' && !teamLeadEmployeeId) }
    );

    const allRows: any[] = useMemo(() => actionPlansData?.data ?? [], [actionPlansData]);

    // ── Filter option lists ────────────────────────────────────────────
    const statusOptions = useMemo(() => {
        const unique = Array.from(new Set<string>(
            allRows
                .map((r: any) => r.coaching_action_plan_status_name)
                .filter((v: any): v is string => !!v && !(via === 'managerCAP' && v === 'Draft'))
        ));
        return unique.map((v) => ({ label: v, value: v }));
    }, [allRows, via]);

    useEffect(() => {
        if (statusOptions.length === 0) {
            setFilterStatus([]);
            return;
        }

        const defaults = ['Open', 'Closed'].filter((s) =>
            statusOptions.some((opt) => opt.value === s)
        );

        setFilterStatus(defaults.length > 0 ? defaults : []);

    }, [statusOptions]);

    const associateOptions = useMemo(() => {
        const unique = Array.from(new Set<string>(
            allRows.map((r: any) => r.employee_name).filter((v: any): v is string => !!v)
        )).sort();
        return unique.map((v) => ({ label: v, value: v }));
    }, [allRows]);

    const metricOptions = useMemo(() => {
        const unique = Array.from(new Set<string>(
            allRows.map((r: any) => r.metric_name).filter((v: any): v is string => !!v)
        )).sort();
        return unique.map((v) => ({ label: v, value: v }));
    }, [allRows]);

    const acknowledgmentOptions = [
        { label: 'Acknowledged', value: 'acknowledged' },
        { label: 'Not Acknowledged', value: 'not_acknowledged' },
    ];
    const commitmentOptions = [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
    ];

    // ── Client-side filtering ──────────────────────────────────────────
    const filtered = useMemo(() => allRows.filter((row) => {
        if (via === 'managerCAP' && row.coaching_action_plan_status_name === 'Draft') return false;
        if (filterStatus.length > 0 && !filterStatus.includes(row.coaching_action_plan_status_name)) return false;
        if (filterAssociate && row.employee_name !== filterAssociate) return false;
        if (filterMetric && row.metric_name !== filterMetric) return false;
        if (filterAcknowledgment) {
            const isAck = !!row.acknowledged;
            if (filterAcknowledgment === 'acknowledged' && !isAck) return false;
            if (filterAcknowledgment === 'not_acknowledged' && isAck) return false;
        }
        if (filterCommitmentAttained) {
            const isAttained = !!row.commitment_attained;
            if (filterCommitmentAttained === 'yes' && !isAttained) return false;
            if (filterCommitmentAttained === 'no' && isAttained) return false;
        }
        return true;
    }), [allRows, filterStatus, filterAssociate, filterMetric,
        filterAcknowledgment, filterCommitmentAttained]);

    // ── Navigation ─────────────────────────────────────────────────────
    const navigateTo = (step: number, row: any, extra: Record<string, any> = {}) => {
        sessionStorage.setItem('cap_nav_intent', 'true');
        navigate('/coachingActionPlan', {
            state: {
                activeStep: step,
                coachingActionPlanId: row.coaching_action_plan_id,
                coachingActionPlanSessionId: row.coaching_action_plan_session_id,
                employeeNumber: row.employee_number,
                employeeName: row.employee_name,
                scorecardVersionId,
                scorecardMetricId: row.scorecard_metric_id,
                coachingNextBestActionId: row.coaching_next_best_action_id,
                metricName: row.metric_name,
                metricCurrentValue: row.metric_current_value ?? null,
                metricTargetValue: row.metric_target_value ?? null,
                metricTypeId: row.metric_type_id,
                metricTypeName: row.metric_type_name,
                metricDirectionId: row.metric_direction_id,
                metricIndex: 0,
                metricStartDate: row.start_date ?? null,
                metricEndDate: row.end_date ?? null,
                ...extra,
            },
        });
    };

    const handleAction = (row: any) => {
        switch (row.coaching_next_best_action_type_id) {
            case CoachingNextBestActionType.StartCoaching:
                navigateTo(STEPS.ANALYZE, row); break;
            case CoachingNextBestActionType.Recognize:
                navigateTo(STEPS.FOLLOW_UP, row); break;
            case CoachingNextBestActionType.Review:
                navigateTo(
                    Number(row.session_number) >= 1 ? STEPS.FOLLOW_UP : STEPS.COACH,
                    row, { isReviewSession: true }
                ); break;
            case CoachingNextBestActionType.FollowUp:
            default:
                navigateTo(STEPS.FOLLOW_UP, row); break;
        }
    };

    // ── Closed CAP read-only view ──────────────────────────────────────
    const [selectedClosedCap, setSelectedClosedCap] = useState<{ coachingActionPlanId: number; sessionId: number } | null>(null);

    const { data: closedCapData } = useGetCAPsessionQuery(
        { coachingActionPlanId: selectedClosedCap?.coachingActionPlanId ?? 0 },
        { skip: !selectedClosedCap }
    );

    const closedCAPSessionData = closedCapData?.data?.[0];

    const handleViewClosedCap = useCallback((row: any) => {
        setSelectedClosedCap({
            coachingActionPlanId: row.coaching_action_plan_id,
            sessionId: row.coaching_action_plan_session_id,
        });
    }, []);

    const handleCloseClosedCapModal = useCallback(() => {
        setSelectedClosedCap(null);
    }, []);

    const handleViewActionPlan = (row: any) => {
        if (setShowAcknowledge) {
            setShowAcknowledge(true);
        }
        if (setActionPlanId) {
            setActionPlanId(row.coaching_action_plan_id);
        }
        if (setSessionId) {
            setSessionId(row.coaching_action_plan_session_id);
        }
    };

    // ── Column templates ───────────────────────────────────────────────
    const associateTemplate = (row: any) => (
        <div className="ap-associate-cell">
            <div className="ap-name">{row.employee_name}</div>
        </div>
    );

    const metricTemplate = (row: any) => (
        <div className="ap-metric-cell">
            <span className="ap-metric-name">{row.metric_name}</span>
        </div>
    );

    const metricResultTemplate = (row: any) => {
        if (row.coaching_action_plan_status_id === 1) return <span className="ap-dash">—</span>;
        const formatted = metricValueFormat(row.metric_result_value, row.metric_type_name);
        const target = row.metric_target_value != null
            ? metricValueFormat(row.metric_target_value, row.metric_type_name) : null;
        const metricAttained = row.commitment_attained;
        const trendIcon = getMetricRecommendationImage(metricAttained ? 1 : 2);

        return (
            <div className="ap-result-cell">
                <span className={`ap-trend-arrow ${metricAttained ? 'attained' : 'not-attained'} ${metricAttained ? 'up' : 'down'}`}>
                    {trendIcon && <img src={trendIcon} alt="trend" width="18" height="18" />}
                </span>
                <span className={`ap-result-value ${metricAttained ? 'attained' : 'not-attained'}`}>{formatted}</span>
                {target && <span className="ap-result-target">/ {target}</span>}
            </div>
        );
    };

    const viewTemplate = (row: any) => (
        <button type="button" className="ap-view-btn" onClick={() => handleViewActionPlan(row)}>
            View | <i className="pi pi-arrow-circle-right" />
        </button>
    );

    const actionTemplate = (row: any) => {
        if (row.coaching_action_plan_status_id === 3){
            return via === 'managerCAP' ? (<span style={{ cursor: 'auto', textAlign: 'center', marginLeft: '15px' }}>- -</span>) : (
                <button type="button" className="ap-view-btn" onClick={() => handleViewClosedCap(row)}>
                    View | <i className="pi pi-eye" />
                </button>
            );
        }
        const label = row.next_best_action_status_name ?? '—';
        const typeId = row.coaching_next_best_action_type_id;
        const classMap: Record<number, string> = {
            [CoachingNextBestActionType.StartCoaching]: 'ap-btn-start',
            [CoachingNextBestActionType.Recognize]: 'ap-btn-recognize',
            [CoachingNextBestActionType.FollowUp]: 'ap-btn-followup',
            [CoachingNextBestActionType.Review]: 'ap-btn-review',
        };
        const btnClass = `ap-action-btn ${classMap[typeId] ?? 'ap-btn-followup'}`;

        if (via === 'managerCAP') {
            return <span className={btnClass} style={{ cursor: 'auto' }}>{label}</span>;
        }
        return (
            <button type="button" className={btnClass} onClick={() => handleAction(row)}>
                {label} | <i className="pi pi-arrow-circle-right" />
            </button>
        );
    };

    const followUpDateTemplate = (row: any) => {
        if (!row.follow_up_date && !row.coaching_complete_date) return <span className="ap-dash">—</span>;
        if (row.coaching_action_plan_status_id === 1) return <span className="ap-dash">—</span>;
        if (row.coaching_action_plan_status_id === 2 && row.follow_up_date)
            return <span className="ap-follow-up">Follow Up on {new Date(row.follow_up_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>;
        if (row.coaching_action_plan_status_id === 3 && row.coaching_complete_date)
            return <span className="ap-follow-up">Closed on {new Date(row.coaching_complete_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>;
        return <span className="ap-dash">—</span>;
    };

    const startDateTemplate = (row: any) => {
        if (!row.start_date) return <span className="ap-dash">—</span>;
        return <span className="ap-start-date">{new Date(row.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>;
    };

    const commitmentTemplate = (row: any) => {
        if (row.coaching_action_plan_status_id === 1) return <span className="ap-dash">—</span>;
        if (row.commitment_attained == null) return <span className="ap-dash">—</span>;
        return row.commitment_attained
            ? <span className="ap-commitment-attained">Yes</span>
            : <span className="ap-commitment-not-attained">No</span>;
    };

    const handleViewCA = (row: any) => {
        navigate(
            `${CAMENU}/${CaMenuTabindex.AssignmentUpdates}/actionplans/${row?.employee_number}/${row?.corrective_action_id}/${row?.ca_start_date}/0`,
            { replace: true }
        );
    };

    const statusTemplate = (row: any) => {
        const hasCA = row.corrective_action_id != null;
        const tooltipId = `ca-tooltip-${row.coaching_action_plan_id}`;
        return (
            <div className="ap-status-cell">
                <span className={`ap-status-${row.coaching_action_plan_status_name}`}>
                    {row.coaching_action_plan_status_name ?? '—'}
                    {hasCA && (
                        <>
                            <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackTop={-60} mouseTrackLeft={10}>
                                <div style={{ fontWeight: 400 }}>
                                    Corrective Action Created on{' '}
                                    <b>{new Date(row.ca_start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</b>
                                    <br />
                                    Severity: <b>{row.ca_severity_level_name}</b>
                                </div>
                            </Tooltip>
                            <img id={tooltipId} src={warningImage} alt="warning" className="ap-status-warning" />
                        </>
                    )}
                </span>
                {hasCA && (
                    <button type="button" className="ap-view-ca-btn" onClick={() => handleViewCA(row)}>
                        View CA <i className="pi pi-arrow-circle-right" style={{ marginLeft: '4px', fontSize: '12px' }} />
                    </button>
                )}
            </div>
        );
    };

    const acknowledgedTemplate = (row: any) => (
        <span className="ap-acknowledged">{row.acknowledged ? 'Yes' : 'No'}</span>
    );

    const currentSessionTemplate = (row: any) => {
        const session = row.session_number;
        if (row.coaching_action_plan_status_id === 1) return <span className="ap-dash">—</span>;
        if (session == null) return <span className="ap-dash">—</span>;
        if (session === 0) return <span className="ap-current-session">Coaching Started</span>;
        return <span className="ap-current-session">Follow Up #{session}</span>;
    };
    const deleteDraft = (e: any, rowData: any) => {
        if (rowData.coaching_action_plan_id) {
            setActionPlanToDelete(rowData.coaching_action_plan_id);
            setShowConfirmationDialog(true);
        }
    };
    const deleteTemplate = (rowData: any) => {
        return (
            rowData?.coaching_action_plan_status_id === 1 ? (
                <Button
                    className={`deleteButton ${actionPlanToDelete === rowData.coaching_action_plan_id && showConfirmationDialog ? 'confirm-delete' : ''}`}
                    type="button"
                    icon="pi pi-trash"
                    onClick={(e: any) => deleteDraft(e, rowData)}
                />
            ) : null
        );
    };
    // ── Main table view ────────────────────────────────────────────────
    return (
        <div className="action-plans-tab">
            <div className="ap-filter-bar">

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">Status</h6>
                    <MultiSelect
                        value={filterStatus}
                        options={statusOptions}
                        onChange={(e) => setFilterStatus(e.value ?? [])}
                        placeholder="Select"
                        display="chip"
                        maxSelectedLabels={2}
                        className="custom-dropdown w-200"
                    />
                </div>

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">Associate</h6>
                    <Dropdown
                        value={filterAssociate}
                        options={associateOptions}
                        onChange={(e) => setFilterAssociate(e.value)}
                        placeholder="Select"
                        showClear={!!filterAssociate}
                        filter
                        className="custom-dropdown w-200"
                    />
                </div>

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">Metric</h6>
                    <Dropdown
                        value={filterMetric}
                        options={metricOptions}
                        onChange={(e) => setFilterMetric(e.value)}
                        placeholder="Select"
                        showClear={!!filterMetric}
                        className="custom-dropdown w-200"
                    />
                </div>

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">Acknowledgment</h6>
                    <Dropdown
                        value={filterAcknowledgment}
                        options={acknowledgmentOptions}
                        onChange={(e) => setFilterAcknowledgment(e.value)}
                        placeholder="Select"
                        showClear={!!filterAcknowledgment}
                        className="custom-dropdown w-200"
                    />
                </div>

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">Commitment Attained</h6>
                    <Dropdown
                        value={filterCommitmentAttained}
                        options={commitmentOptions}
                        onChange={(e) => setFilterCommitmentAttained(e.value)}
                        placeholder="Select"
                        showClear={!!filterCommitmentAttained}
                        className="custom-dropdown w-200"
                    />
                </div>

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">Date From</h6>
                    <Calendar
                        value={fromDateFilter}
                        onChange={(e) => e.value instanceof Date && setFromDateFilter(e.value)}
                        dateFormat="M dd, yy"
                        maxDate={toDateFilter}
                        showIcon
                        className="custom-calendar w-200"
                    />
                </div>

                <div className="ap-filter-item">
                    <h6 className="ap-filter-label">To</h6>
                    <Calendar
                        value={toDateFilter}
                        onChange={(e) => e.value instanceof Date && setToDateFilter(e.value)}
                        dateFormat="M dd, yy"
                        minDate={fromDateFilter}
                        maxDate={new Date()}
                        showIcon
                        className="custom-calendar w-200"
                    />
                </div>

            </div>

            <DataTable
                value={filtered}
                loading={isLoading}
                paginator={filtered.length > 10}
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                dataKey="coaching_action_plan_id"
                emptyMessage={
                    <div style={{ textAlign: 'center', width: '100%', fontSize: '16px' }}>
                        {emptyMessage || 'No action plans found.'}
                    </div>
                }
                className="ap-datatable p-datatable-sm"
                responsiveLayout="scroll"
            >
                {via === 'managerCAP' && (
                    <Column header="" body={viewTemplate} style={{ minWidth: '100px' }} />
                )}
                <Column header="Associate" body={associateTemplate} sortable sortField="employee_name" style={{ minWidth: '200px' }} />
                <Column header="Coached Metric" body={metricTemplate} style={{ minWidth: '150px' }} />
                <Column header="Metric Result" body={metricResultTemplate} style={{ minWidth: '140px' }} />
                <Column header="Action" body={actionTemplate} style={{ minWidth: '160px' }} />
                <Column header="Follow Up / Closed Date" body={followUpDateTemplate} style={{ minWidth: '170px' }} />
                <Column header="Commitment Attained" body={commitmentTemplate} style={{ minWidth: '160px' }} />
                <Column header="Status" body={statusTemplate} style={{ minWidth: '110px' }} />
                <Column header="Coaching Start Date" body={startDateTemplate} style={{ minWidth: '160px' }} />
                <Column header="Current Session" body={currentSessionTemplate} style={{ minWidth: '130px' }} />
                <Column header="Acknowledged" body={acknowledgedTemplate} style={{ minWidth: '150px' }} />
                {via !== 'managerCAP' && (
                    <Column header="" body={deleteTemplate} style={{ minWidth: '100px' }} />
                )}
            </DataTable>

            {selectedClosedCap && closedCAPSessionData &&
                ReactDOM.createPortal(
                    <SessionReadOnlyModal
                        sessionId={selectedClosedCap.sessionId}
                        sessionTitle="Closed Coaching Action Plan"
                        CAPSessionData={closedCAPSessionData}
                        onClose={handleCloseClosedCapModal}
                    />,
                    document.body
                )}
            <DeleteDraftCAP
                coachingActionPlanId={actionPlanToDelete ?? 0}
                showDialog={showConfirmationDialog}
                setShowDialog={setShowConfirmationDialog}
            />
        </div>
    );
};

export default ActionPlans;